<template>
    <viewer-tab ref="tab" :title="`Properties | ${partLabel}`" :icon="$icons.propsAtoms"
                v-loading="loadingTab" element-loading-text="Saving"
                :url-help="$urls.HELP_MODEL_PROPS" :click-x="clickX" :accept="handleAccept">
        <div slot="header-buttons" class="header-buttons">
            <el-switch
                    style="margin-right: 5px"
                    v-model="iso"
                    inactive-text="AGMA"
                    active-text="ISO"
                    inactive-color="var(--color)"
                    active-color="var(--color)"
                    @change="handleStandardChange">
            </el-switch>
            <el-popover
                    placement="bottom"
                    width="500"
                    trigger="click"
                    transition="none">
                <table>
                    <tr>
                        <th style="width: 190px">Name</th>
                        <th>Description</th>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://www.iso.org/obp/ui/#iso:std:iso:6336:-5:ed-2:v1:en"
                                     target="_blank" type="primary" :underline="false">
                                ISO 6336-5:2003
                            </el-link>
                        </td>
                        <td>Strength and quality of materials</td>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://www.iso.org/obp/ui/#iso:std:iso:6336:-5:ed-3:v1:en"
                                     target="_blank" type="primary" :underline="false">
                                ISO 6336-5:2016
                            </el-link>
                        </td>
                        <td>Strength and quality of materials</td>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://www.iso.org/obp/ui/#iso:std:iso:1328:-1:ed-2:v1:en"
                                     target="_blank" type="primary" :underline="false">
                                ISO 1328-1:2013
                            </el-link>
                        </td>
                        <td>Definitions and allowable values of deviations relevant to flanks of gear teeth</td>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://www.iso.org/obp/ui#iso:std:iso:1328:-2:ed-2:v1:en"
                                     target="_blank" type="primary" :underline="false">
                                ISO 1328-2:2020
                            </el-link>
                        </td>
                        <td>Definitions and allowable values of double flank radial composite deviations</td>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://webstore.ansi.org/standards/agma/ansiagma2001d04r2016"
                                     target="_blank" type="primary" :underline="false">
                                ANSI/AGMA 2001-D04
                            </el-link>
                        </td>
                        <td>Fundamental rating factors and calculation methods for involute spur and helical gear teeth</td>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://webstore.ansi.org/standards/agma/ansiagmaiso1328b14"
                                     target="_blank" type="primary" :underline="false">
                                ANSI/AGMA ISO 1328-1-B14
                            </el-link>
                        </td>
                        <td>Definitions and allowable values of deviations relevant to flanks of gear teeth</td>
                    </tr>
                    <tr>
                        <td>
                            <el-link href="https://webstore.ansi.org/standards/agma/ansiagma2015b15"
                                     target="_blank" type="primary" :underline="false">
                                ANSI/AGMA 2015-2-B15
                            </el-link>
                        </td>
                        <td>Definitions and allowable values of double flank radial composite deviations</td>
                    </tr>
                </table>
                <el-button
                        slot="reference"
                        class="menu-icon-btn"
                        type="text"
                        aria-label="clear">
                    <i class="el-icon-takeaway-box"/>
                </el-button>
            </el-popover>
        </div>
        <!--PLACEHOLDER {readability} split ISO and AGMA modelers into separate components-->
        <div v-show="iso" class="pane-grid-1-2" v-loading="loadingProperties" element-loading-text="Loading properties">
            <el-collapse class="pane-1" v-model="activeCollapseIso" @change="handleCollapseChangeIso" accordion>
                <el-collapse-item :name="$options.collapse.iso6336_5_2003">
                    <span slot="title" class="collapse-header">
                        <span>
                            Ferrous Material
                            <i v-show="!isIso6336_5_2003" class="el-icon-check"/>
                            <i v-show="isIso6336_5_2003" class="el-icon-check check-active"/>
                        </span>
                        <span>ISO 6336-5:2003</span>
                    </span>
                    <el-form ref="formIso633652003" :model="formIso" class="ml-2" size="mini" :label-width="$options.labelWidth">
                        <el-form-item class="form-item" prop="iso6336_5_2003.Id" label="Type and quality">
                            <el-select v-model="formIso.iso6336_5_2003.Id"
                                       placeholder="Select material"
                                       class="w-100"
                                       clearable
                                       filterable
                                       default-first-option
                                       @change="handleChangeMaterialIso">
                                <el-option
                                        v-for="item in materials.iso6336_5_2003"
                                        :key="item.Id"
                                        :label="item.label"
                                        :value="item.Id">
                                </el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item class="form-item" prop="iso6336_5_2003.surfaceHardnessNumber" label="Surface hardness">
                            <el-input v-model="formIso.iso6336_5_2003.surfaceHardnessNumber" @change="handleUpdateMaterialIso">
                                <template slot="suffix">{{ dataIso6336_5_2003.surfaceHardnessScale }}</template>
                            </el-input>
                        </el-form-item>
                        <el-form-item class="form-item" prop="iso6336_5_2003.shotPeen">
                            <span slot="label">
                                <el-tooltip placement="bottom-start" :tabindex="-1" :open-delay="$defaults.tooltipDelay">
                                    <div slot="content">
                                        "Shot peening increases residual compressive stresses and improves bending strength in the roots of gear teeth."<br>
                                        Increases bending stress limits for carburized case hardened gears of MQ (10%) or ME (5%) quality.
                                    </div>
                                    <span>Shot peen <small><i :class="$icons.chatBox"/></small></span>
                                </el-tooltip>
                            </span>
                            <el-checkbox v-model="formIso.iso6336_5_2003.shotPeen" @change="handleUpdateMaterialIso" :disabled="disableShotPeen"/>
                        </el-form-item>
                    </el-form>
                </el-collapse-item>
                <el-collapse-item :name="$options.collapse.iso1328_1_2013" class="collapse-item">
                    <span slot="title" class="collapse-header">
                        <span>
                            Flank Quality
                            <i v-show="!formIso.iso1328_1_2013_class" class="el-icon-check"/>
                            <i v-show="formIso.iso1328_1_2013_class" class="el-icon-check check-active"/>
                        </span>
                        <span>ISO 1328-1:2013</span>
                    </span>
                    <div class="ml-2">
                        <el-form :model="formIso" size="mini" label-position="top" :label-width="$options.labelWidth">
                            <el-form-item label="Tolerance class" prop="iso1328_1_2013_class">
                                <el-select v-model="formIso.iso1328_1_2013_class"
                                           placeholder="Select class"
                                           class="w-100"
                                           clearable
                                           filterable
                                           default-first-option
                                           @change="handleChangeQualityFlankIso">
                                    <el-option
                                            v-for="item in toleranceClasses.iso132812013"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        <span class="select-option-label1">{{ item }}</span>
                                        <span class="select-option-label2">{{ labelQualityLevel(item, toleranceClasses.iso132812013) }}</span>
                                    </el-option>
                                </el-select>
                            </el-form-item>
                        </el-form>
                    </div>
                </el-collapse-item>
                <el-collapse-item :name="$options.collapse.iso1328_2_2020" class="collapse-item">
                    <span slot="title" class="collapse-header">
                        <span>
                            Radial Quality
                            <i v-show="!formIso.iso1328_2_2020_class" class="el-icon-check"/>
                            <i v-show="formIso.iso1328_2_2020_class" class="el-icon-check check-active"/>
                        </span>
                        <span>ISO 1328-2:2020</span>
                    </span>
                    <el-form :model="formIso" size="mini" label-position="top" :label-width="$options.labelWidth">
                        <el-form-item label="Tolerance class" prop="iso1328_2_2020_class">
                            <el-select v-model="formIso.iso1328_2_2020_class"
                                       placeholder="Select class"
                                       class="w-100"
                                       clearable
                                       filterable
                                       default-first-option
                                       @change="handleChangeQualityRadialIso">
                                <el-option
                                        v-for="item in toleranceClasses.iso132822020"
                                        :key="item"
                                        :label="item"
                                        :value="item">
                                    <span class="select-option-label1">{{ item }}</span>
                                    <span class="select-option-label2">{{ labelQualityLevel(item, toleranceClasses.iso132822020) }}</span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-form>
                </el-collapse-item>
            </el-collapse>
            <div class="pane-2" v-loading="loadingPane2">
                <el-select v-model="figureIso"
                           class="w-100"
                           size="mini"
                           filterable
                           default-first-option
                           @change="handleFigureChangeIso"
                           :disabled="!activeCollapseIso">
                    <el-option
                            v-for="item in figuresIso"
                            :key="item.filename"
                            :label="item.label"
                            :value="item.filename">
                    </el-option>
                </el-select>
                <!--NOTE: v-show used so that a single chart instance is created, then updated accordingly.-->
                <div v-show="activeCollapseIso" class="figure-wrapper">
                    <div v-show="isFigureIsoSN" class="chart-wrapper">
                        <canvas ref="chartIso"/>
                    </div>
                    <img v-if="!isFigureIsoSN" :src="figureIsoSrc" alt="FAILED TO LOAD"/>
                </div>
                <div v-show="!activeCollapseIso" class="figure-empty">
                    <i class="el-icon-s-data"/>
                </div>
            </div>
            <div class="pane-3 overflow-y" v-loading="loadingPane3">
                <modeler-table-params :table="tableIso" :empty-text="tableIsoEmptyText"/>
            </div>
        </div>
        <div v-show="!iso" class="pane-grid-1-2" v-loading="loadingProperties" element-loading-text="Loading properties">
            <el-collapse class="pane-1" v-model="activeCollapseAgma" @change="handleCollapseChangeAgma" accordion>
                <el-collapse-item :name="$options.collapse.agma2001_d04">
                    <span slot="title" class="collapse-header">
                        <span>
                            Ferrous Material
                            <i v-show="!isAgma2001_D04" class="el-icon-check"/>
                            <i v-show="isAgma2001_D04" class="el-icon-check check-active"/>
                        </span>
                        <span>ANSI/AGMA 2001-D04</span>
                    </span>
                    <el-form ref="formAgma2001D04" :model="formAgma" class="ml-2" size="mini" :label-width="$options.labelWidth">
                        <el-form-item class="form-item" prop="agma2001_d04.Id" label="Type and quality">
                            <el-select v-model="formAgma.agma2001_d04.Id"
                                       placeholder="Designation, treatment, grade  |  surf. hardness"
                                       class="w-100"
                                       clearable
                                       filterable
                                       default-first-option
                                       @change="handleChangeMaterialAgma">
                                <el-option
                                        v-for="item in materials.agma2001_d04"
                                        :key="item.Id"
                                        :label="item.label"
                                        :value="item.Id">
                                    <span class="select-option-label1">{{ item.label }}</span>
                                    <span class="select-option-label2">{{ item.labelSurfaceHardness }}</span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item class="form-item" prop="agma2001_d04.coreHardnessNumber" label="Core hardness">
                            <el-input v-model="formAgma.agma2001_d04.coreHardnessNumber"
                                      :disabled="!dataAgma2001_D04.coreHardnessVariable"
                                      @change="handleUpdateMaterialAgma">
                                <template slot="suffix">{{ dataAgma2001_D04.coreHardnessScale }}</template>
                            </el-input>
                        </el-form-item>
                    </el-form>
                </el-collapse-item>
                <el-collapse-item :name="$options.collapse.agma1328_1_b14" class="collapse-item">
                    <span slot="title" class="collapse-header">
                        <span>
                            Flank Quality
                            <i v-show="!formAgma.agma1328_1_b14_class" class="el-icon-check"/>
                            <i v-show="formAgma.agma1328_1_b14_class" class="el-icon-check check-active"/>
                        </span>
                        <span>ANSI/AGMA ISO 1328-1-B14</span>
                    </span>
                    <el-form :model="formAgma" class="ml-2" size="mini" label-position="top" :label-width="$options.labelWidth">
                        <el-form-item label="Tolerance class" prop="agma1328_1_b14_class">
                            <el-select v-model="formAgma.agma1328_1_b14_class"
                                       placeholder="Select class"
                                       class="w-100"
                                       clearable
                                       filterable
                                       default-first-option
                                       @change="handleChangeQualityFlankAgma">
                                <el-option
                                        v-for="item in toleranceClasses.agma13281B14"
                                        :key="item"
                                        :label="item"
                                        :value="item">
                                    <span class="select-option-label1">{{ item }}</span>
                                    <span class="select-option-label2">{{ labelQualityLevel(item, toleranceClasses.agma13281B14) }}</span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-form>
                </el-collapse-item>
                <el-collapse-item :name="$options.collapse.agma2015_2_b15" class="collapse-item">
                    <span slot="title" class="collapse-header">
                        <span>
                            Radial Quality
                            <i v-show="!formAgma.agma2015_2_b15_class" class="el-icon-check"/>
                            <i v-show="formAgma.agma2015_2_b15_class" class="el-icon-check check-active"/>
                        </span>
                        <span>ANSI/AGMA 2015-1-B15</span>
                    </span>
                    <el-form :model="formAgma" class="ml-2" size="mini" label-position="top" :label-width="$options.labelWidth">
                        <el-form-item label="Tolerance class" prop="agma2015_2_b15_class">
                            <el-select v-model="formAgma.agma2015_2_b15_class"
                                       placeholder="Select class"
                                       class="w-100"
                                       clearable
                                       filterable
                                       default-first-option
                                       @change="handleChangeQualityRadialAgma">
                                <el-option
                                        v-for="item in toleranceClasses.agma20152B15"
                                        :key="item"
                                        :label="item"
                                        :value="item">
                                    <span class="select-option-label1">{{ item }}</span>
                                    <span class="select-option-label2">{{ labelQualityLevel(item, toleranceClasses.agma20152B15) }}</span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-form>
                </el-collapse-item>
            </el-collapse>
            <div class="pane-2" v-loading="loadingPane2">
                <el-select v-model="figureAgma"
                           class="w-100"
                           size="mini"
                           filterable
                           default-first-option
                           @change="handleFigureChangeAgma"
                           :disabled="!activeCollapseAgma">
                    <el-option
                            v-for="item in figuresAgma"
                            :key="item.filename"
                            :label="item.label"
                            :value="item.filename">
                    </el-option>
                </el-select>
                <!--NOTE: v-show used so that a single chart instance is created, then updated accordingly.-->
                <div v-show="activeCollapseAgma" class="figure-wrapper">
                    <div v-show="isFigureAgmaSN" class="chart-wrapper">
                        <canvas ref="chartAgma"/>
                    </div>
                    <img v-if="!isFigureAgmaSN" :src="figureAgmaSrc" alt="FAILED TO LOAD"/>
                </div>
                <div v-show="!activeCollapseAgma" class="figure-empty">
                    <i class="el-icon-s-data"/>
                </div>
            </div>
            <div class="pane-3 overflow-y" v-loading="loadingPane3">
                <modeler-table-params :table="tableAgma" :empty-text="tableAgmaEmptyText"/>
            </div>
        </div>
    </viewer-tab>
</template>

<script>
  import ViewerTab from "../viewer/ViewerTab";
  import MatTableParams from "../_tables/MatTableParams";
  import ModelerTableParams from "../_tables/ModelerTableParams";
  import {copyDeep, assignProps, nullifyStr} from "../../assets/js/utils";
  import {points} from "../../assets/js/coord";
  import math from "../../assets/js/math";
  import gear from "../../assets/js/gear";
  import {getObjectById} from "../../store/helpers";
  import {apolloMutate, apolloQuery} from "../../config/apollo";
  import requests from "../../requests";
  import gql from "graphql-tag";
  import Chart from "chart.js";

  export default {
    name: "ModelGearProps",
    components: {ModelerTableParams, MatTableParams, ViewerTab},
    props: {
      clickX: Function,
      openId: String,
      name: String,  // unique tab name
    },

    labelWidth: '135px',
    collapse: {
      iso6336_5_2003: 'iso6336_5_2003',
      iso6336_5_2016: 'iso6336_5_2016',
      iso1328_1_2013: 'iso1328_1_2013',
      iso1328_2_2020: 'iso1328_2_2020',
      agma2001_d04: 'agma2001_d04',
      agma1328_1_b14: 'agma1328_1_b14',
      agma2015_2_b15: 'agma2015_2_b15',
    },
    messages: {
      noProperties: "Select material or quality",
      noMaterial: "Select a material specification",
      noQuality: "Select a tolerance class"
    },
    figures: {
      // NOTE: Some figures may have been excluded for various reasons.
      iso6336_5_2003: [{
        label: 'Stress-cycle curve for pitting',
        filename: 'SN-pitting'  // not actually filename, but used as identifier
      }, {
        label: 'Stress-cycle curve for bending',
        filename: 'SN-bending'  // not actually filename, but used as identifier
      }, {
        label: 'Figure 1a - Allowable stress numbers (contact) for wrought normalized low carbon steels',
        filename: 'iso6336-5-2003_fig01a.png'
      }, {
        label: 'Figure 1b - Allowable stress numbers (contact) for cast steels',
        filename: 'iso6336-5-2003_fig01b.png'
      }, {
        label: 'Figure 2a - Nominal and allowable stress numbers (bending) for wrought normalized low carbon steels',
        filename: 'iso6336-5-2003_fig02a.png'
      }, {
        label: 'Figure 2b - Nominal and allowable stress numbers (bending) for cast steels',
        filename: 'iso6336-5-2003_fig02b.png'
      }, {
        label: 'Figure 3a - Allowable stress numbers (contact) for black malleable cast iron',
        filename: 'iso6336-5-2003_fig03a.png'
      }, {
        label: 'Figure 3b - Allowable stress numbers (contact) for nodular cast iron',
        filename: 'iso6336-5-2003_fig03b.png'
      }, {
        label: 'Figure 3c - Allowable stress numbers (contact) for grey cast iron',
        filename: 'iso6336-5-2003_fig03c.png'
      }, {
        label: 'Figure 4a - Nominal and allowable stress numbers (bending) for black malleable cast iron',
        filename: 'iso6336-5-2003_fig04a.png'
      }, {
        label: 'Figure 4b - Nominal and allowable stress numbers (bending) for nodular cast iron',
        filename: 'iso6336-5-2003_fig04b.png'
      }, {
        label: 'Figure 4c - Nominal and allowable stress numbers (bending) for grey cast iron',
        filename: 'iso6336-5-2003_fig04c.png'
      }, {
        label: 'Figure 5 - Allowable stress numbers (contact) for through hardened wrought steels',
        filename: 'iso6336-5-2003_fig05.png'
      }, {
        label: 'Figure 6 - Nominal and allowable stress numbers (bending) for through hardened wrought steels',
        filename: 'iso6336-5-2003_fig06.png'
      }, {
        label: 'Figure 7 - Allowable stress numbers (contact) for through hardened cast steels',
        filename: 'iso6336-5-2003_fig07.png'
      }, {
        label: 'Figure 8 - Nominal and allowable stress numbers (bending) for through hardened cast steels',
        filename: 'iso6336-5-2003_fig08.png'
      }, {
        label: 'Figure 9 - Allowable stress numbers (contact) for case hardened wrought steels',
        filename: 'iso6336-5-2003_fig09.png'
      }, {
        label: 'Figure 10 - Nominal and allowable stress numbers (bending) for case hardened wrought steels',
        filename: 'iso6336-5-2003_fig10.png'
      }, {
        label: 'Figure 11 - Allowable stress numbers (contact) for flame or induction hardened wrought and cast steels',
        filename: 'iso6336-5-2003_fig11.png'
      }, {
        label: 'Figure 12 - Nominal and allowable stress numbers (bending) for flame or induction hardened wrought and cast steels',
        filename: 'iso6336-5-2003_fig12.png'
      }, {
        label: 'Figure 13a - Allowable stress numbers (contact) for nitriding steels: hardened, tempered and gas nitrided',
        filename: 'iso6336-5-2003_fig13a.png'
      }, {
        label: 'Figure 13b - Allowable stress numbers (contact) for through hardening steels: hardened, tempered and gas nitrided',
        filename: 'iso6336-5-2003_fig13b.png'
      }, {
        label: 'Figure 14a - Nominal and allowable stress numbers (bending) for nitriding steels: hardened, tempered and gas nitrided',
        filename: 'iso6336-5-2003_fig14a.png'
      }, {
        label: 'Figure 14b - Nominal and allowable stress numbers (bending) for through hardening steels: hardened, tempered and gas nitrided',
        filename: 'iso6336-5-2003_fig14b.png'
      }, {
        label: 'Figure 15 - Allowable stress numbers (contact) for wrought steels nitrocarburized',
        filename: 'iso6336-5-2003_fig15.png'
      }, {
        label: 'Figure 16 - Nominal and allowable stress numbers (bending) for wrought steels nitrocarburized',
        filename: 'iso6336-5-2003_fig16.png'
      }, {
        label: 'Figure 17 - Recommended values of case depth',
        filename: 'iso6336-5-2003_fig17.png'
      }, {
        label: 'Figure 18 - Recommended values of nitride case depth',
        filename: 'iso6336-5-2003_fig18.png'
      }, {
        label: 'Figure 19 - Core hardness coefficient for nitrided gearing',
        filename: 'iso6336-5-2003_fig19.png'
      }],
      iso1328_1_2013: [{
        label: 'Figure 2 - Pitch deviations',
        filename: 'iso1328-1-2013_fig02_pitch-deviations.png'
      }, {
        label: 'Figure 3 - Measured profile',
        filename: 'iso1328-1-2013_fig03_measured-profile.png',
      }, {
        label: 'Figure 4 - Profile deviations with unmodified involute',
        filename: 'iso1328-1-2013_fig04_profile-deviation-unmodified.png'
      }, {
        label: 'Figure 5 - Profile deviations with pressure angle modified',
        filename: 'iso1328-1-2013_fig05_profile-deviation-profile-slope.png'
      }, {
        label: 'Figure 6 - Profile deviations with profile crowning modification',
        filename: 'iso1328-1-2013_fig06_profile-deviation-profile-crown.png'
      }, {
        label: 'Figure 7 - Profile deviations with profile modified with tip relief',
        filename: 'iso1328-1-2013_fig07_profile-deviation-tip-relief.png'
      }, {
        label: 'Figure 8 - Profile deviations with profile modified with tip and root relief',
        filename: 'iso1328-1-2013_fig08_profile-deviation-tip-and-root-relief.png'
      }, {
        label: 'Figure 9 - Helix deviations with unmodified helix',
        filename: 'iso1328-1-2013_fig09_helix-deviation-unmodified.png'
      }, {
        label: 'Figure 10 - Helix deviations with helix angle modification',
        filename: 'iso1328-1-2013_fig10_helix-deviation-lead-slope.png'
      }, {
        label: 'Figure 11 - Helix deviations with helix crowning modification',
        filename: 'iso1328-1-2013_fig11_helix-deviation-lead-crown.png'
      }, {
        label: 'Figure 12 - Helix deviations with helix end relief',
        filename: 'iso1328-1-2013_fig12_helix-deviation-end-relief.png'
      }, {
        label: 'Figure 13 - Helix deviations with modified helix angle with end relief',
        filename: 'iso1328-1-2013_fig13_helix-deviation-lead-slope-end-relief.png'
      }, {
        label: 'Figure 14 - Profile excess',
        filename: 'iso1328-1-2013_fig14_profile-excess.png'
      }, {
        label: 'Figure 15 - Helix excess',
        filename: 'iso1328-1-2013_fig15_helix-excess.png'
      }, {
        label: 'Figure A.1 - Regression zones for profile with tip and root modification',
        filename: 'iso1328-1-2013_figA01_profile-regression-zones-tip-root-modification.png'
      }, {
        label: 'Figure A.2 - Zone-based evaluation for profile with tip and root modifications',
        filename: 'iso1328-1-2013_figA02_profile-zone-based-evaluation-tip-root.png'
      }, {
        label: 'Figure A.3 - Regression zones for a helix with relief at both ends',
        filename: 'iso1328-1-2013_figA03_lead-regression-zones-end-relief.png'
      }, {
        label: 'Figure A.4 - Zone-based evaluation for a helix with end modifications',
        filename: 'iso1328-1-2013_figA04_lead-zone-based-evaluation-end-relief.png'
      }, {
        label: 'Figure B.1 - Profile crowning',
        filename: 'iso1328-1-2013_figB01_profile-crowning.png'
      }, {
        label: 'Figure B.2 - Second order profile deviations',
        filename: 'iso1328-1-2013_figB02_second-order-profile-deviations.png'
      }, {
        label: 'Figure B.3 - Second order helix deviations',
        filename: 'iso1328-1-2013_figB03_second-order-helix-deviations.png'
      }, {
        label: 'Figure D.1 - Sector pitch deviation and pitch span deviation',
        filename: 'iso1328-1-2013_figD01_sector-pitch-and-pitch-span-deviation.png'
      }, {
        label: 'Figure E.1 - Runout diagram of a gear with 16 teeth',
        filename: 'iso1328-1-2013_figE01_runout-diagram.png'
      }, {
        label: 'Figure E.2 - Runout from pitch measurement',
        filename: 'iso1328-1-2013_figE02_runout-from-pitch-measurement.png'
      }, {
        label: 'Figure F.1 - Schematic view of single flank tester',
        filename: 'iso1328-1-2013_figF01_single-flank-tester.png'
      }, {
        label: 'Figure F.2 - An example of transmission error',
        filename: 'iso1328-1-2013_figF02_example-transmission-error.png'
      }, {
        label: 'Figure F.3 - High-pass filtered single flank composite deviations',
        filename: 'iso1328-1-2013_figF03_high-pass-filtered-composite-deviations.png'
      }, {
        label: 'Figure F.4 - Fourier transformed single flank composite deviations',
        filename: 'iso1328-1-2013_figF04_fourier-transformed-composite-deviations.png'
      }, {
        label: 'Figure G.1 - Adjacent pitch difference',
        filename: 'iso1328-1-2013_figG01_adjacent-pitch-difference.png'
      }],
      iso1328_2_2020: [{
        label: 'Figure 1 - Tooth-to-tooth radial composite deviation',
        filename: 'iso1328-2-2020_fig01_tooth-to-tooth-radial-deviation.png'
      }, {
        label: 'Figure 2 - Total radial composite deviation',
        filename: 'iso1328-2-2020_fig02_total-radial-composite-deviation.png'
      }, {
        label: 'Figure B.1 - Double flank radial composite deviation over four teeth for a 50 tooth gear',
        filename: 'iso1328-2-2020_figB01_double-flank-radial-composite-deviation.png'
      }],
      agma2001_d04: [{
        label: 'Stress-cycle curve for pitting',
        filename: 'SN-pitting'  // not actually filename, but used as identifier
      }, {
        label: 'Stress-cycle curve for bending',
        filename: 'SN-bending'  // not actually filename, but used as identifier
      }, {
        label: 'Figure 8 - Allowable contact stress number for through hardened steel gears',
        filename: 'agma2001-D04_fig08.png'
      }, {
        label: 'Figure 9 - Allowable bending stress number for through hardened steel gears',
        filename: 'agma2001-D04_fig09.png'
      }, {
        label: 'Figure 10 - Allowable bending stress numbers for nitrided through hardened steel gears',
        filename: 'agma2001-D04_fig10.png'
      }, {
        label: 'Figure 11 - Allowable bending stress numbers for nitriding steel gears',
        filename: 'agma2001-D04_fig11.png'
      }, {
        label: 'Figure 12-1 - Variations in hardening pattern obtainable on gear teeth with spin hardening',
        filename: 'agma2001-D04_fig12-1.png'
      }, {
        label: 'Figure 12-2 - Variations in hardening pattern obtainable on gear teeth with flank hardening',
        filename: 'agma2001-D04_fig12-2.png'
      }, {
        label: 'Figure 12-3 - Variations in hardening pattern obtainable on gear teeth with flank and root hardening',
        filename: 'agma2001-D04_fig12-3.png'
      }, {
        label: 'Figure 13 - Minimum effective case depth for carburized gears',
        filename: 'agma2001-D04_fig13.png'
      }, {
        label: 'Figure 14 - Core hardness coefficient',
        filename: 'agma2001-D04_fig14.png'
      }, {
        label: 'Figure 15 - Minimum total case depth for nitrided gears',
        filename: 'agma2001-D04_fig15.png'
      }, {
        label: 'Figure 16 - Allowable yield strength number for steel gears',
        filename: 'agma2001-D04_fig16.png'
      }, {
        label: 'Figure 17 - Pitting resistance stress cycle factor',
        filename: 'agma2001-D04_fig17.png'
      }, {
        label: 'Figure 18 - Bending strength stress cycle factor',
        filename: 'agma2001-D04_fig18.png'
      }],
      agma1328_1_b14: [{
        label: 'Figure 2 - Pitch deviations',
        filename: 'iso1328-1-2013_fig02_pitch-deviations.png'
      }, {
        label: 'Figure 3 - Measured profile',
        filename: 'iso1328-1-2013_fig03_measured-profile.png',
      }, {
        label: 'Figure 4 - Profile deviations with unmodified involute',
        filename: 'iso1328-1-2013_fig04_profile-deviation-unmodified.png'
      }, {
        label: 'Figure 5 - Profile deviations with pressure angle modified',
        filename: 'iso1328-1-2013_fig05_profile-deviation-profile-slope.png'
      }, {
        label: 'Figure 6 - Profile deviations with profile crowning modification',
        filename: 'iso1328-1-2013_fig06_profile-deviation-profile-crown.png'
      }, {
        label: 'Figure 7 - Profile deviations with profile modified with tip relief',
        filename: 'iso1328-1-2013_fig07_profile-deviation-tip-relief.png'
      }, {
        label: 'Figure 8 - Profile deviations with profile modified with tip and root relief',
        filename: 'iso1328-1-2013_fig08_profile-deviation-tip-and-root-relief.png'
      }, {
        label: 'Figure 9 - Helix deviations with unmodified helix',
        filename: 'iso1328-1-2013_fig09_helix-deviation-unmodified.png'
      }, {
        label: 'Figure 10 - Helix deviations with helix angle modification',
        filename: 'iso1328-1-2013_fig10_helix-deviation-lead-slope.png'
      }, {
        label: 'Figure 11 - Helix deviations with helix crowning modification',
        filename: 'iso1328-1-2013_fig11_helix-deviation-lead-crown.png'
      }, {
        label: 'Figure 12 - Helix deviations with helix end relief',
        filename: 'iso1328-1-2013_fig12_helix-deviation-end-relief.png'
      }, {
        label: 'Figure 13 - Helix deviations with modified helix angle with end relief',
        filename: 'iso1328-1-2013_fig13_helix-deviation-lead-slope-end-relief.png'
      }, {
        label: 'Figure 14 - Profile excess',
        filename: 'iso1328-1-2013_fig14_profile-excess.png'
      }, {
        label: 'Figure 15 - Helix excess',
        filename: 'iso1328-1-2013_fig15_helix-excess.png'
      }, {
        label: 'Figure A.1 - Regression zones for profile with tip and root modification',
        filename: 'iso1328-1-2013_figA01_profile-regression-zones-tip-root-modification.png'
      }, {
        label: 'Figure A.2 - Zone-based evaluation for profile with tip and root modifications',
        filename: 'iso1328-1-2013_figA02_profile-zone-based-evaluation-tip-root.png'
      }, {
        label: 'Figure A.3 - Regression zones for a helix with relief at both ends',
        filename: 'iso1328-1-2013_figA03_lead-regression-zones-end-relief.png'
      }, {
        label: 'Figure A.4 - Zone-based evaluation for a helix with end modifications',
        filename: 'iso1328-1-2013_figA04_lead-zone-based-evaluation-end-relief.png'
      }, {
        label: 'Figure B.1 - Profile crowning',
        filename: 'iso1328-1-2013_figB01_profile-crowning.png'
      }, {
        label: 'Figure B.2 - Second order profile deviations',
        filename: 'iso1328-1-2013_figB02_second-order-profile-deviations.png'
      }, {
        label: 'Figure B.3 - Second order helix deviations',
        filename: 'iso1328-1-2013_figB03_second-order-helix-deviations.png'
      }, {
        label: 'Figure D.1 - Sector pitch deviation and pitch span deviation',
        filename: 'iso1328-1-2013_figD01_sector-pitch-and-pitch-span-deviation.png'
      }, {
        label: 'Figure E.1 - Runout diagram of a gear with 16 teeth',
        filename: 'iso1328-1-2013_figE01_runout-diagram.png'
      }, {
        label: 'Figure E.2 - Runout from pitch measurement',
        filename: 'iso1328-1-2013_figE02_runout-from-pitch-measurement.png'
      }, {
        label: 'Figure F.1 - Schematic view of single flank tester',
        filename: 'iso1328-1-2013_figF01_single-flank-tester.png'
      }, {
        label: 'Figure F.2 - An example of transmission error',
        filename: 'iso1328-1-2013_figF02_example-transmission-error.png'
      }, {
        label: 'Figure F.3 - High-pass filtered single flank composite deviations',
        filename: 'iso1328-1-2013_figF03_high-pass-filtered-composite-deviations.png'
      }, {
        label: 'Figure F.4 - Fourier transformed single flank composite deviations',
        filename: 'iso1328-1-2013_figF04_fourier-transformed-composite-deviations.png'
      }, {
        label: 'Figure G.1 - Adjacent pitch difference',
        filename: 'iso1328-1-2013_figG01_adjacent-pitch-difference.png'
      }],
      agma2015_2_b15: [{
        label: 'Figure 1 - Radial composite deviation diagram for a 50 tooth gear',
        filename: 'agma2015-2-B15_fig01_radial-composite-deviation-50z.png',
      }, {
        label: 'Figure 2 - Illustration of AGMA classification specification',
        filename: 'agma2015-2-B15_fig02_agma-classification-specification.png',
      }, {
        label: 'Figure 3 - Illustration of AGMA classification specification for individual tolerance elements',
        filename: 'agma2015-2-B15_fig03_agma-classification-individual-tols.png',
      }, {
        label: 'Figure 4 - Illustration of AGMA classification specification for individual tolerance values',
        filename: 'agma2015-2-B15_fig04_agma-classification-individual-vals.png',
      }, {
        label: 'Figure B.1 - Radial composite deviation over four teeth for the 50 tooth gear from Figure 1',
        filename: 'agma2015-2-B15_figB01_radial-composite-deviation-50z-4k.png',
      }],
      none: [{label: 'Figures of gear properties', filename: 'foo.bar'}],
    },
    chart: {
      nullData: math.logspace(10, 1000).map(x => {return {x: x, y: 0}}),
    },

    data() {
      return {
        // APOLLO
        part: null,
        gear: null,
        gearId: '',
        materials: {
          iso6336_5_2003: [],
          agma2001_d04: [],
        },
        toleranceClasses: {
          iso132812013: [],
          iso132822020: [],
          agma13281B14: [],
          agma20152B15: []
        },
        loadingTab: false,
        loadingProperties: false,
        loadingPane2: false,
        loadingPane3: false,

        // VIEW
        iso: false,
        activeCollapseIso: '',
        activeCollapseAgma: '',

        // FORMS
        formIso: {
          iso6336_5_2003: {
            Id: '',
            shotPeen: false,
            surfaceHardnessNumber: '',
          },
          iso1328_1_2013_class: '',
          iso1328_2_2020_class: '',
        },
        formAgma: {
          agma2001_d04: {
            Id: '',
            coreHardnessNumber: '',
          },
          agma1328_1_b14_class: '',
          agma2015_2_b15_class: '',
        },

        // TABULAR DATA
        dataIso1328_1_2013: {},
        dataIso1328_2_2020: {},
        dataAgma1328_1_B14: {},
        dataAgma2015_2_B15: {},

        // CHARTS
        chartIso: null,
        chartAgma: null,

        // FIGURES
        figureIso: '',
        figureAgma: '',
        figureIso6336_5_2003: null,
        figureIso1328_1_2013: null,
        figureIso1328_2_2020: null,
        figureAgma2001_D04: null,
        figureAgma1328_1_B14: null,
        figureAgma2015_2_B15: null,
      }
    },
    computed: {
      partLabel() {
        return this.part ? this.$handlers.model.partLabel(this.part) : '';
      },
      // ISO PROPERTIES
      isIso6336_5_2003() {
        // Boolean to indicate if ISO material is selected; created since Id=0 exists.
        return this.formIso.iso6336_5_2003.Id !== '';
      },
      dataIso6336_5_2003() {
        return this.formIso.iso6336_5_2003.Id !== '' ? getObjectById(this.materials.iso6336_5_2003, this.formIso.iso6336_5_2003.Id) : {};
      },
      disableShotPeen() {
        // Per section 6.7 of ISO 6336-5:2003, disable shot peening if not case hardened MQ or ME quality.

        const isCaseHardened = this.dataIso6336_5_2003.type === 'Case hardened wrought steel';
        const isML = this.dataIso6336_5_2003.quality === 'ML';  // ML, MQ, ME
        return !isCaseHardened || isML;
      },
      tableIso6336_5_2003() {
        // PLACEHOLDER {SN} concatenate table data for material stress limits (static, limited life, long life)
        return this.$handlers.gear.tableIso633652003(this.dataIso6336_5_2003);
      },
      tableIso1328_1_2013() {
        return this.$handlers.gear.tableIso132812013(this.dataIso1328_1_2013);
      },
      tableIso1328_2_2020() {
        return this.$handlers.gear.tableIso132822020(this.dataIso1328_2_2020);
      },
      tableIso() {
        switch (this.activeCollapseIso) {
          case this.$options.collapse.iso6336_5_2003:
            return this.tableIso6336_5_2003;
          case this.$options.collapse.iso1328_1_2013:
            return this.tableIso1328_1_2013;
          case this.$options.collapse.iso1328_2_2020:
            return this.tableIso1328_2_2020;
          default:
            return [];
        }
      },
      tableIsoEmptyText() {
        switch (this.activeCollapseIso) {
          case this.$options.collapse.iso6336_5_2003:
            return this.$options.messages.noMaterial;
          case this.$options.collapse.iso1328_1_2013:
          case this.$options.collapse.iso1328_2_2020:
            return this.$options.messages.noQuality;
          default:
            return this.$options.messages.noProperties;
        }
      },
      figuresIso() {
        switch (this.activeCollapseIso) {
          case this.$options.collapse.iso6336_5_2003:
            return this.$options.figures.iso6336_5_2003;
          case this.$options.collapse.iso1328_1_2013:
            return this.$options.figures.iso1328_1_2013;
          case this.$options.collapse.iso1328_2_2020:
            return this.$options.figures.iso1328_2_2020;
          default:
            return this.$options.figures.none;
        }
      },
      figureIsoSrc() {
        // Get local file of figure to display.
        // Alternatively, retrieve figures from a public bucket.

        switch (this.activeCollapseIso) {
          case this.$options.collapse.iso6336_5_2003:
            return require(`../../assets/img/iso6336_5_2003/${this.figureIso}`);
          case this.$options.collapse.iso1328_1_2013:
            return require(`../../assets/img/iso1328_1_2013/${this.figureIso}`);
          case this.$options.collapse.iso1328_2_2020:
            return require(`../../assets/img/iso1328_2_2020/${this.figureIso}`);
        }
      },
      isFigureIsoSN() {
        return this.figureIso.startsWith('SN');
      },
      isFigureIsoPitting() {
        return this.figureIso === 'SN-pitting';
      },
      // AGMA PROPERTIES
      isAgma2001_D04() {
        // Boolean to indicate if AGMA material is selected; created since Id=0 exists.
        return this.formAgma.agma2001_d04.Id !== '';
      },
      dataAgma2001_D04() {
        return this.formAgma.agma2001_d04.Id !== '' ? getObjectById(this.materials.agma2001_d04, this.formAgma.agma2001_d04.Id) : {};
      },
      tableAgma2001_D04() {
        // PLACEHOLDER {SN} concatenate table data for material stress limits (static, limited life, long life)
        return this.$handlers.gear.tableAgma2001D04(this.dataAgma2001_D04);
      },
      tableAgma1328_1_B14() {
        return this.$handlers.gear.tableAgma13281B14(this.dataAgma1328_1_B14);
      },
      tableAgma2015_2_B15() {
        return this.$handlers.gear.tableAgma20152B15(this.dataAgma2015_2_B15);
      },
      tableAgma() {
        switch (this.activeCollapseAgma) {
          case this.$options.collapse.agma2001_d04:
            return this.tableAgma2001_D04;
          case this.$options.collapse.agma1328_1_b14:
            return this.tableAgma1328_1_B14;
          case this.$options.collapse.agma2015_2_b15:
            return this.tableAgma2015_2_B15;
          default:
            return [];
        }
      },
      tableAgmaEmptyText() {
        switch (this.activeCollapseAgma) {
          case this.$options.collapse.agma2001_d04:
            return this.$options.messages.noMaterial;
          case this.$options.collapse.agma1328_1_b14:
          case this.$options.collapse.agma2015_2_b15:
            return this.$options.messages.noQuality;
          default:
            return this.$options.messages.noProperties;
        }
      },
      figuresAgma() {
        switch (this.activeCollapseAgma) {
          case this.$options.collapse.agma2001_d04:
            return this.$options.figures.agma2001_d04;
          case this.$options.collapse.agma1328_1_b14:
            return this.$options.figures.agma1328_1_b14;
          case this.$options.collapse.agma2015_2_b15:
            return this.$options.figures.agma2015_2_b15;
          default:
            return this.$options.figures.none;
        }
      },
      figureAgmaSrc() {
        // Get local file of figure to display.
        // Alternatively, retrieve figures from a public bucket.

        switch (this.activeCollapseAgma) {
          case this.$options.collapse.agma2001_d04:
            return require(`../../assets/img/agma2001_d04/${this.figureAgma}`);
          case this.$options.collapse.agma1328_1_b14:
            // Identical to ISO 1328-1:2013.
            return require(`../../assets/img/iso1328_1_2013/${this.figureAgma}`);
          case this.$options.collapse.agma2015_2_b15:
            return require(`../../assets/img/agma2015_2_b15/${this.figureAgma}`);
        }
      },
      isFigureAgmaSN() {
        return this.figureAgma.startsWith('SN');
      },
      isFigureAgmaPitting() {
        return this.figureAgma === 'SN-pitting';
      },
    },
    apollo: {
      gear: {
        skip() { return !this.gearId },
        fetchPolicy: 'cache-and-network',
        variables() { return { Id: this.gearId } },
        query: gql`query getGearProperties ($Id: ID!) {
            gear: gearDetail (Id: $Id) {
              Id
              name
              geom {
                ${requests.gear.GearDetailGeom}
              }
              material {
                ${requests.gear.GearMaterial}
              }
              quality {
                ${requests.gear.GearQuality}
              }
            }
          }`,
        result({ data }) {
          // deep copy to avoid mutation of Apollo cache data
          let gear = copyDeep(data.gear);

          // map to form data
          this.mapMaterial2Forms(gear.material);
          this.mapQuality2Forms(gear.quality);

          // initialize charts
          this.chartAgma = this.newChartLogLog(this.$refs.chartAgma);
          this.chartIso = this.newChartLogLog(this.$refs.chartIso);

          // trigger material change event
          this.updateMaterialAgmaCharts(true);
          this.updateMaterialIsoCharts(true);
        },
      },
    },

    async mounted() {
      // Called every time the tab is opened.
      // Also see @loaded event handler.
      //
      // NOTES:
      //    1. Parts retrieved with apollo watcher instead of here so that it is reactive to part CRUD.
      //    2. Dialog windows do not benefit from such reactivity, but a viewer tab may benefit.

      // get part being edited
      this.part = this.$store.getters.getPart(this.openId);
      this.$store.commit('SET_VIEWER_TAB_TITLE', this.partLabel);

      // query quality and material options
      await this.queryMaterialsAndQualities();

      // trigger query of gear to edit; must be after materials query for proper initialization
      this.gearId = this.part.obj.Id;

      // initialize figures
      const initFigure = (figArr) => figArr[0].filename;
      this.figureIso = initFigure(this.$options.figures.none);
      this.figureAgma = initFigure(this.$options.figures.none);
      this.figureIso6336_5_2003 = initFigure(this.$options.figures.iso6336_5_2003);
      this.figureIso1328_1_2013 = initFigure(this.$options.figures.iso1328_1_2013);
      this.figureIso1328_2_2020 = initFigure(this.$options.figures.iso1328_2_2020);
      this.figureAgma2001_D04 = initFigure(this.$options.figures.agma2001_d04);
      this.figureAgma1328_1_B14 = initFigure(this.$options.figures.agma1328_1_b14);
      this.figureAgma2015_2_B15 = initFigure(this.$options.figures.agma2015_2_b15);
    },
    methods: {
      // VIEW
      handleStandardChange() {
        // Call when the standard being viewed has changed.

        if (this.iso) {
          this.handleCollapseChangeIso(this.activeCollapseIso);
        } else {
          this.handleCollapseChangeAgma(this.activeCollapseAgma);
        }
      },
      updateStatusMessage(message) {
        // Mutate global state to display status message.

        this.$store.commit('SET_STATUS', {
          message: message,
          loading: false,
        });
        this.$store.commit('TOGGLE_VIEWER_TAB');
      },
      handleCollapseChangeIso(activeName) {
        // Call when the property being viewed has changed.

        let message = '';
        switch (activeName) {
          case this.$options.collapse.iso6336_5_2003:
            message = `Gear material per ${this.$handlers.gear.MATERIAL_LABEL.iso633652003}`;
            this.figureIso = this.figureIso6336_5_2003;
            break;
          case this.$options.collapse.iso1328_1_2013:
            this.handleChangeQualityFlankIso(this.formIso.iso1328_1_2013_class);
            message = `Gear quality per ${this.$handlers.gear.QUALITY_LABEL.iso132812013}`;
            this.figureIso = this.figureIso1328_1_2013;
            break;
          case this.$options.collapse.iso1328_2_2020:
            this.handleChangeQualityRadialIso(this.formIso.iso1328_2_2020_class);
            message = `Gear quality per ${this.$handlers.gear.QUALITY_LABEL.iso132822020}`;
            this.figureIso = this.figureIso1328_2_2020;
            break;
        }
        this.updateStatusMessage(message);
      },
      handleCollapseChangeAgma(activeName) {
        // Call when the property being viewed has changed.

        let message = '';
        switch (activeName) {
          case this.$options.collapse.agma2001_d04:
            message = `Gear material per ${this.$handlers.gear.MATERIAL_LABEL.agma2001D04}`;
            this.figureAgma = this.figureAgma2001_D04;
            break;
          case this.$options.collapse.agma1328_1_b14:
            this.handleChangeQualityFlankAgma(this.formAgma.agma1328_1_b14_class);
            message = `Gear quality per ${this.$handlers.gear.QUALITY_LABEL.agma13281B14}`;
            this.figureAgma = this.figureAgma1328_1_B14;
            break;
          case this.$options.collapse.agma2015_2_b15:
            this.handleChangeQualityRadialAgma(this.formAgma.agma2015_2_b15_class);
            message = `Gear quality per ${this.$handlers.gear.QUALITY_LABEL.agma20152B15}`;
            this.figureAgma = this.figureAgma2015_2_B15;
            break;
        }
        this.updateStatusMessage(message);
      },
      handleFigureChangeIso(figure) {
        // On change of figure, cache the selected figure option.

        switch (this.activeCollapseIso) {
          case this.$options.collapse.iso6336_5_2003:
            this.figureIso6336_5_2003 = figure;
            // if material and SN-curve selected, then update chart
            if (this.isFigureIsoSN) this.updateMaterialIsoCharts();
            break;
          case this.$options.collapse.iso1328_1_2013:
            this.figureIso1328_1_2013 = figure;
            break;
          case this.$options.collapse.iso1328_2_2020:
            this.figureIso1328_2_2020 = figure;
            break;
        }
      },
      handleFigureChangeAgma(figure) {
        // On change of figure, cache the selected figure option.

        switch (this.activeCollapseAgma) {
          case this.$options.collapse.agma2001_d04:
            this.figureAgma2001_D04 = figure;
            // if SN-curve selected, then update chart
            if (this.isFigureAgmaSN) this.updateMaterialAgmaCharts();
            break;
          case this.$options.collapse.agma1328_1_b14:
            this.figureAgma1328_1_B14 = figure;
            break;
          case this.$options.collapse.agma2015_2_b15:
            this.figureAgma2015_2_B15 = figure;
            break;
        }
      },

      // APOLLO --> FORMS
      async mapMaterial2Forms(material) {
        // args: `material` field from persisted gear properties

        if (!material) {
          this.$refs.formIso633652003.resetFields();
          this.$refs.formAgma2001D04.resetFields();
        } else {
          // map ISO 6336-5:2003 to local form
          if (!material.iso633652003) {
            this.$refs.formIso633652003.resetFields();
          } else {
            assignProps(this.formIso.iso6336_5_2003, material.iso633652003);
          }

          // map AGMA 2001-D04 to local form
          if (!material.agma2001D04) {
            this.$refs.formAgma2001D04.resetFields();
          } else {
            assignProps(this.formAgma.agma2001_d04, material.agma2001D04);
          }
        }
      },
      async mapQuality2Forms(quality) {
        // args: `quality` field from persisted gear properties

        if (!quality) {
          this.formIso.iso1328_1_2013_class = '';
          this.formIso.iso1328_2_2020_class = '';
          this.formAgma.agma1328_1_b14_class = '';
          this.formAgma.agma2015_2_b15_class = '';
        } else {
          // map ISO tolerance classes to local form
          this.formIso.iso1328_1_2013_class = quality.iso132812013 ? quality.iso132812013.toleranceClass : '';
          this.formIso.iso1328_2_2020_class = quality.iso132822020 ? quality.iso132822020.toleranceClass : '';

          // map AGMA tolerance classes to local form
          this.formAgma.agma1328_1_b14_class = quality.agma13281B14 ? quality.agma13281B14.toleranceClass : '';
          this.formAgma.agma2015_2_b15_class = quality.agma20152B15 ? quality.agma20152B15.toleranceClass : '';
        }
      },

      // APOLLO <-- FORMS
      mapForms2Material() {
        // Synthesize the API mutation payload for material.
        // Returns null if material is undefined.

        const iso633652003 = !this.isIso6336_5_2003 ? null : {
          Id: this.formIso.iso6336_5_2003.Id,
          surfaceHardnessNumber: this.formIso.iso6336_5_2003.surfaceHardnessNumber,
          shotPeen: this.formIso.iso6336_5_2003.shotPeen,
        };
        const agma2001D04 = !this.isAgma2001_D04 ? null : {
          Id: this.formAgma.agma2001_d04.Id,
          coreHardnessNumber: this.formAgma.agma2001_d04.coreHardnessNumber,
        };

        const anyDefined = [iso633652003, agma2001D04].some(Boolean);

        return !anyDefined ? null : {iso633652003, agma2001D04};
      },
      mapForms2Quality() {
        // Synthesize the API mutation payload for quality.
        // Returns null if quality is undefined.

        const iso132812013 = nullifyStr(this.formIso.iso1328_1_2013_class);
        const iso132822020 = nullifyStr(this.formIso.iso1328_2_2020_class);
        const agma13281B14 = nullifyStr(this.formAgma.agma1328_1_b14_class);
        const agma20152B15 = nullifyStr(this.formAgma.agma2015_2_b15_class);

        const anyDefined = [iso132812013, iso132822020, agma13281B14, agma20152B15].some(Boolean);

        return !anyDefined ? null : {iso132812013, iso132822020, agma13281B14, agma20152B15}
      },
      async handleAccept() {
        // Call API mutation to update gear material and quality, then close tab.
        // Allows partially defined properties for a given standard.

        this.loadingTab = true;
        const mutate = {
          variables: {
            data: {
              Id: this.gearId,
              material: this.mapForms2Material(),
              quality: this.mapForms2Quality()
            },
          },
          mutation: gql`mutation updateGearDetailProps ($data: UpdateGearDetailInput!) {
            updateGearDetail (data: $data) {
              gearDetail {
                Id
                material {
                  ${this.$requests.gear.GearMaterial}
                }
                quality {
                  ${this.$requests.gear.GearQuality}
                }
              }
            }
          }`,
        };
        await apolloMutate(mutate).finally(() => this.loadingTab = false);
        this.clickX();
      },

      // QUERIES
      async queryMaterialsAndQualities() {
        // API query of all materials and tolerance classes.

        const query = {
          fetchPolicy: "cache-first",
          query: gql`query getQualitiesAndMaterials {
            gearToleranceClasses {
              iso132812013
              iso132822020
              agma13281B14
              agma20152B15
            }
            materialsAgma2001D04 {
              ${this.$requests.eng.MaterialDataAGMA2001_D04}
            }
            materialsIso633652003 {
              ${this.$requests.eng.MaterialDataISO6336_5_2003}
            }
          }`,
        };

        this.loadingProperties = true;
        const {data: {gearToleranceClasses, materialsIso633652003, materialsAgma2001D04}} = await apolloQuery(query)
          .finally(() => this.loadingProperties = false);

        // map to local state
        this.toleranceClasses = gearToleranceClasses;
        this.materials.agma2001_d04 = materialsAgma2001D04.map(mat => {
          return {
            ...mat,
            label: `${mat.designation}, ${mat.heatTreatment}, ${mat.grade}`,
            labelSurfaceHardness: `${mat.surfaceHardnessMin} ${mat.surfaceHardnessScale}`,
          }
        });
        this.materials.iso6336_5_2003 = materialsIso633652003.map(mat => {
          let label = `${mat.type}, ${mat.quality}`;
          if (mat.coreHardnessMin) label += ` (${mat.coreHardnessMin} ${mat.coreHardnessScale} core)`;
          return {...mat, label};
        });
      },
      async queryLoadingPane3(query) {
        this.loadingPane3 = true;
        return apolloQuery(query).finally(() => this.loadingPane3 = false);
      },
      async queryLoadingRHS(query) {
        this.loadingPane2 = true;
        this.loadingPane3 = true;
        return apolloQuery(query).finally(() => {
          this.loadingPane2 = false;
          this.loadingPane3 = false;
        });
      },

      // QUERIES - QUALITY
      async queryAgma1328_1_B14(toleranceClass) {
        // API query to compute tolerances per AGMA 1328-1-B14.
        // Returns quality data from query response.

        // compute query variables
        const numberOfTeeth = this.gear.geom.numberOfTeeth;
        const moduleTransverse = gear.moduleTransverse1(this.gear.geom);
        const referenceDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});

        const query = {
          fetchPolicy: "cache-first",
          variables: {
            toleranceClass: toleranceClass,
            moduleNormal: this.gear.geom.moduleNormal,
            referenceDiameter: referenceDiameter,
            numberOfTeeth: numberOfTeeth,
            facewidth: this.gear.geom.facewidth,
          },
          query: gql`query computeTolerancesAgma13281B14 ($toleranceClass: Int!, $moduleNormal: Float!, $referenceDiameter: Float!, $numberOfTeeth: Int!, $facewidth: Float!) {
            qualityAgma13281B14 (toleranceClass: $toleranceClass, moduleNormal: $moduleNormal, referenceDiameter: $referenceDiameter, numberOfTeeth: $numberOfTeeth, facewidth: $facewidth) {
              ${this.$requests.eng.QualityDataAGMA1328_1_B14}
            }
          }`,
        };

        const {data: {qualityAgma13281B14}} = await this.queryLoadingPane3(query);
        return qualityAgma13281B14;
      },
      async queryAgma2015_2_B15(toleranceClass) {
        // API query to compute tolerances per AGMA 2015-2-B15.
        // Returns quality data from query response.

        const query = {
          fetchPolicy: "cache-first",
          variables: {
            toleranceClass: toleranceClass,
            moduleNormal: this.gear.geom.moduleNormal,
            numberOfTeeth: this.gear.geom.numberOfTeeth,
            helixAngle: this.gear.geom.helixAngle,
          },
          query: gql`query computeTolerancesAgma20152B15 ($toleranceClass: Int!, $moduleNormal: Float!, $numberOfTeeth: Int!, $helixAngle: Float!) {
            qualityAgma20152B15 (toleranceClass: $toleranceClass, moduleNormal: $moduleNormal, numberOfTeeth: $numberOfTeeth, helixAngle: $helixAngle) {
              ${this.$requests.eng.QualityDataAGMA2015_2_B15}
            }
          }`,
        };

        const {data: {qualityAgma20152B15}} = await this.queryLoadingPane3(query);
        return qualityAgma20152B15;
      },
      async queryIso1328_1_2013(toleranceClass) {
        // API query to compute tolerances per ISO 1328-1:2013.
        // Returns quality data from query response.

        // compute query variables
        const numberOfTeeth = this.gear.geom.numberOfTeeth;
        const moduleTransverse = gear.moduleTransverse1(this.gear.geom);
        const referenceDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});

        const query = {
          fetchPolicy: "cache-first",
          variables: {
            toleranceClass: toleranceClass,
            moduleNormal: this.gear.geom.moduleNormal,
            referenceDiameter: referenceDiameter,
            numberOfTeeth: numberOfTeeth,
            facewidth: this.gear.geom.facewidth,
          },
          query: gql`query computeTolerancesIso132812013 ($toleranceClass: Int!, $moduleNormal: Float!, $referenceDiameter: Float!, $numberOfTeeth: Int!, $facewidth: Float!) {
            qualityIso132812013 (toleranceClass: $toleranceClass, moduleNormal: $moduleNormal, referenceDiameter: $referenceDiameter, numberOfTeeth: $numberOfTeeth, facewidth: $facewidth) {
              ${this.$requests.eng.QualityDataISO1328_1_2013}
            }
          }`,
        };

        const {data: {qualityIso132812013}} = await this.queryLoadingPane3(query);
        return qualityIso132812013;
      },
      async queryIso1328_2_2020(toleranceClass) {
        // API query to compute tolerances per ISO 1328-2:2020.
        // Returns quality data from query response.

        const query = {
          fetchPolicy: "cache-first",
          variables: {
            toleranceClass: toleranceClass,
            moduleNormal: this.gear.geom.moduleNormal,
            numberOfTeeth: this.gear.geom.numberOfTeeth,
            helixAngle: this.gear.geom.helixAngle,
          },
          query: gql`query computeTolerancesIso132822020 ($toleranceClass: Int!, $moduleNormal: Float!, $numberOfTeeth: Int!, $helixAngle: Float!) {
            qualityIso132822020 (toleranceClass: $toleranceClass, moduleNormal: $moduleNormal, numberOfTeeth: $numberOfTeeth, helixAngle: $helixAngle) {
              ${this.$requests.eng.QualityDataISO1328_2_2020}
            }
          }`,
        };

        const {data: {qualityIso132822020}} = await this.queryLoadingPane3(query);
        return qualityIso132822020;
      },

      // QUERIES - MATERIAL
      async queryMaterialAgma(Id) {
        // DEPRECATED not required since all material data is queried in queryMaterialsAndQualities
        const query = {
          fetchPolicy: "cache-first",
          variables: {
            Id: Id,
          },
          query: gql`query getMaterialAgma ($Id: Int!) {
            materialAgma2001D04 (Id: $Id) {
              ${this.$requests.eng.MaterialDataAGMA2001_D04}
            }
          }`,
        };
        const {data: {materialAgma}} = await apolloQuery(query);
        return materialAgma;
      },
      async queryMaterialIso(Id) {
        // DEPRECATED not required since all material data is queried in queryMaterialsAndQualities
        const query = {
          fetchPolicy: "cache-first",
          variables: {
            Id: Id,
          },
          query: gql`query getMaterialIso ($Id: Int!) {
            materialIso633652003 (Id: $Id) {
              ${this.$requests.eng.MaterialDataISO6336_5_2003}
            }
          }`,
        };
        const {data: {materialIso}} = await apolloQuery(query);
        return materialIso;
      },
      async queryStressLimitsAgma() {
        // API query of pitting and bending stress limits per AGMA 2001-D04.
        // Returns object {pitting stress limits, bending stress limits}.

        const query = {
          fetchPolicy: "cache-first",
          variables: {
            materialId: this.formAgma.agma2001_d04.Id,
            coreHardnessNumber: this.formAgma.agma2001_d04.coreHardnessNumber,
          },
          query: gql`query computeStressLimitsAgma ($materialId: Int!, $coreHardnessNumber: Float) {
            stressLimitsPittingAgma2001D04 (materialId: $materialId, coreHardnessNumber: $coreHardnessNumber) {
              ${this.$requests.eng.stressLimitsPittingAgma2001D04}
            }
            stressLimitsBendingAgma2001D04 (materialId: $materialId, coreHardnessNumber: $coreHardnessNumber) {
              ${this.$requests.eng.stressLimitsBendingAgma2001D04}
            }
          }`,
        };

        const {data: {stressLimitsPittingAgma2001D04, stressLimitsBendingAgma2001D04}} = await this.queryLoadingRHS(query);
        return {stressLimitsPittingAgma2001D04, stressLimitsBendingAgma2001D04};
      },
      async queryStressLimitsIso() {
        // API query of pitting and bending stress limits per ISO 6336-2/3:2006.
        // Returns object {pitting stress limits, bending stress limits}.

        const query = {
          fetchPolicy: "cache-first",
          variables: {
            materialId: this.formIso.iso6336_5_2003.Id,
            surfaceHardnessNumber: this.formIso.iso6336_5_2003.surfaceHardnessNumber,
            shotPeen: this.formIso.iso6336_5_2003.shotPeen,
          },
          query: gql`query computeStressLimitsIso ($materialId: Int!, $surfaceHardnessNumber: Float, $shotPeen: Boolean) {
            stressLimitsIso633622006 (materialId: $materialId, surfaceHardnessNumber: $surfaceHardnessNumber, shotPeen: $shotPeen) {
              ${this.$requests.eng.stressLimitsIso633622006}
            }
            stressLimitsIso633632006 (materialId: $materialId, surfaceHardnessNumber: $surfaceHardnessNumber, shotPeen: $shotPeen) {
              ${this.$requests.eng.stressLimitsIso633632006}
            }
          }`,
        };

        const {data: {stressLimitsIso633622006, stressLimitsIso633632006}} = await this.queryLoadingRHS(query);
        return {stressLimitsIso633622006, stressLimitsIso633632006};
      },

      // QUALITY
      labelQualityLevel(cls, classes) {
        // Returns description of a specified tolerance class.

        const best = classes[0];
        const worst = classes[classes.length-1];

        switch (cls) {
          case best:
            return '(best quality)';
          case worst:
            return '(worst quality)';
        }
      },
      async handleChangeQualityFlankAgma(toleranceClass) {
        // Handle the selection of flank tolerance class per AGMA 1328-1-B14.
        // Calls API to compute tolerances and update local state for tabular display.

        this.dataAgma1328_1_B14 = !toleranceClass ? {} : await this.queryAgma1328_1_B14(toleranceClass);
      },
      async handleChangeQualityRadialAgma(toleranceClass) {
        // Handle the selection of radial tolerance class per AGMA 2015-2-B15.
        // Calls API to compute tolerances and update local state for tabular display.

        this.dataAgma2015_2_B15 = !toleranceClass ? {} : await this.queryAgma2015_2_B15(toleranceClass);
      },
      async handleChangeQualityFlankIso(toleranceClass) {
        // Handle the selection of flank tolerance class per ISO 1328-1-2013.
        // Calls API to compute tolerances and update local state for tabular display.

        this.dataIso1328_1_2013 = !toleranceClass ? {} : await this.queryIso1328_1_2013(toleranceClass);
      },
      async handleChangeQualityRadialIso(toleranceClass) {
        // Handle the selection of radial tolerance class per ISO 1328-2-2020.
        // Calls API to compute tolerances and update local state for tabular display.

        this.dataIso1328_2_2020 = !toleranceClass ? {} : await this.queryIso1328_2_2020(toleranceClass);
      },

      // MATERIAL - EVENTS
      handleChangeMaterialAgma() {
        // Handle the change of AGMA material.

        // default core hardness to its minimum value
        this.formAgma.agma2001_d04.coreHardnessNumber = this.dataAgma2001_D04.coreHardnessMin;

        // update material plot(s)
        this.updateMaterialAgmaCharts();
      },
      handleUpdateMaterialAgma() {
        // Handle the change of AGMA material properties.
        // PLACEHOLDER {UX} add coreHardnessMax prop to AGMA 2001-D04 per plots; requires backend update

        // force core hardness to be within min/max values
        if (this.formAgma.agma2001_d04.coreHardnessNumber < this.dataAgma2001_D04.coreHardnessMin) {
          this.formAgma.agma2001_d04.coreHardnessNumber = this.dataAgma2001_D04.coreHardnessMin;
        }

        // update material plot(s)
        this.updateMaterialAgmaCharts();
      },
      handleChangeMaterialIso() {
        // Handle the change of ISO material.

        // default surface hardness to its minimum value
        this.formIso.iso6336_5_2003.surfaceHardnessNumber = this.dataIso6336_5_2003.surfaceHardnessMin;

        // clear shot peening if disabled
        if (this.disableShotPeen) this.formIso.iso6336_5_2003.shotPeen = false;

        // update material plot(s)
        this.updateMaterialIsoCharts()
      },
      handleUpdateMaterialIso() {
        // Handle the change of ISO material properties.

        // force hardness to be within min/max values
        if (this.formIso.iso6336_5_2003.surfaceHardnessNumber < this.dataIso6336_5_2003.surfaceHardnessMin) {
          this.formIso.iso6336_5_2003.surfaceHardnessNumber = this.dataIso6336_5_2003.surfaceHardnessMin;
        } else if (this.formIso.iso6336_5_2003.surfaceHardnessNumber > this.dataIso6336_5_2003.surfaceHardnessMax) {
          this.formIso.iso6336_5_2003.surfaceHardnessNumber = this.dataIso6336_5_2003.surfaceHardnessMax;
        }

        // update material plot(s)
        this.updateMaterialIsoCharts();
      },

      // MATERIAL - CHARTS
      newChartLogLog(ctx) {
        // Helper function for creating new Chart.js instance with log-log scale.

        let chart = new Chart(ctx, {
          type: 'line',
          data: {
            datasets: [{
              ...this.$defaults.chart.lineDataset,
              backgroundColor: this.$defaults.colors.backgroundAlpha,
              data: this.$options.chart.nullData,
            }, {
              ...this.$defaults.chart.lineDataset,
              borderColor: '#a41010',
              borderWidth: 2,
              backgroundColor: this.$defaults.colors.backgroundAlpha,
              data: this.$options.chart.nullData,
            }],
          },
          options: {
            ...this.$defaults.chart.options,
            scales: {
              xAxes: [{
                type: 'logarithmic',
                display: true,
              }],
              yAxes: [{
                type: 'logarithmic',
                display: true,
              }],
            },
          }
        });

        // configure chart defaults and resize callback
        chart.options.elements.point.hitRadius = 3;
        chart.options.elements.line.tension = 0;
        chart.options.elements.line.fill = 'start';

        return chart;
      },
      resetChart(chart) {
        chart.data.datasets[0].data = this.$options.chart.nullData;
        chart.data.datasets[1].data = this.$options.chart.nullData;
        chart.update();
      },
      async updateMaterialAgmaCharts(pitting=false) {
        // Query stress limits based on selected material and properties, then update chart data.
        // PLACEHOLDER {material} update AGMA plot of contact & bending stress limits vs hardness; chart.js

        if (!this.isAgma2001_D04) {
          this.resetChart(this.chartAgma);
        } else {
          const {stressLimitsPittingAgma2001D04, stressLimitsBendingAgma2001D04} = await this.queryStressLimitsAgma();

          // region COMPUTE SN-CURVE
          // console.log('stressLimitsPittingAgma2001D04', stressLimitsPittingAgma2001D04);
          // console.log('stressLimitsBendingAgma2001D04', stressLimitsBendingAgma2001D04);

          let nStatic, nLimitedLife, nLongLife, sigmaStatic, sigmaLimitedLife, sigmaLongLifeGeneral, sigmaLongLifeCritical;

          if (this.isFigureAgmaPitting || pitting) {
            const {static: statics, limitedLife, longLifeGeneral, longLifeCritical} = stressLimitsPittingAgma2001D04;

            nStatic = statics.loadCycles;
            nLimitedLife = limitedLife.loadCycles;
            nLongLife = longLifeGeneral.loadCycles;

            sigmaStatic = statics.pittingStressLimit;
            sigmaLimitedLife = limitedLife.pittingStressLimit;
            sigmaLongLifeGeneral = longLifeGeneral.pittingStressLimit;
            sigmaLongLifeCritical = longLifeCritical.pittingStressLimit;
          } else {
            const {static: statics, limitedLife, longLifeGeneral, longLifeCritical} = stressLimitsBendingAgma2001D04;

            nStatic = statics.loadCycles;
            nLimitedLife = limitedLife.loadCycles;
            nLongLife = longLifeGeneral.loadCycles;

            sigmaStatic = statics.bendingStressLimit;
            sigmaLimitedLife = limitedLife.bendingStressLimit;
            sigmaLongLifeGeneral = longLifeGeneral.bendingStressLimit;
            sigmaLongLifeCritical = longLifeCritical.bendingStressLimit;
          }

          // load cycle arrays
          const cyclesStatic = [0, nStatic];
          const cyclesLimitedLife = math.logspace(nStatic, nLimitedLife);
          const cyclesLongLife = math.logspace(nLimitedLife, nLongLife);

          // define functions to compute stress limits along SN curve
          const stressesLimitedLifeFcn = math.loglogFcn(nStatic, sigmaStatic, nLimitedLife, sigmaLimitedLife);
          const stressesLongLifeGeneralFcn = math.loglogFcn(nLimitedLife, sigmaLimitedLife, nLongLife, sigmaLongLifeGeneral);
          const stressesLongLifeCriticalFcn = math.loglogFcn(nLimitedLife, sigmaLimitedLife, nLongLife, sigmaLongLifeCritical);

          const stressesStatic = cyclesStatic.map(() => sigmaStatic);
          const stressesLimitedLife = cyclesLimitedLife.map(x => stressesLimitedLifeFcn(x));
          const stressesLongLifeGeneral = cyclesLongLife.map(x => stressesLongLifeGeneralFcn(x));
          const stressesLongLifeCritical = cyclesLongLife.map(x => stressesLongLifeCriticalFcn(x));

          // concatenate and create points array
          const loadCycles = [...cyclesStatic, ...cyclesLimitedLife, ...cyclesLongLife];
          const stressLimits = [...stressesStatic, ...stressesLimitedLife, ...stressesLongLifeGeneral];
          const stressLimitsCritical = [...stressesStatic, ...stressesLimitedLife, ...stressesLongLifeCritical];

          // endregion

          // update chart data
          this.chartAgma.data.datasets[0].data = points(loadCycles, stressLimits);
          this.chartAgma.data.datasets[1].data = points(loadCycles, stressLimitsCritical);
          this.chartAgma.update();
        }
      },
      async updateMaterialIsoCharts(pitting=false) {
        // Query stress limits based on selected material and properties, then update chart data.
        // PLACEHOLDER {material} update ISO plot of contact & bending stress limits vs hardness; chart.js

        if (!this.isIso6336_5_2003) {
          this.resetChart(this.chartIso);
        } else {
          const {stressLimitsIso633622006, stressLimitsIso633632006} = await this.queryStressLimitsIso();

          // region COMPUTE SN-CURVE
          // console.log('stressLimitsIso633622006', stressLimitsIso633622006);
          // console.log('stressLimitsIso633632006', stressLimitsIso633632006);

          let loadCycles, stressLimits, stressLimitsCritical;

          if (this.isFigureIsoPitting || pitting) {
            const {static: statics, limitedLife1, limitedLife2, longLifeGeneral, longLifeCritical} = stressLimitsIso633622006;

            const nStatic = statics.loadCycles;
            const nLimitedLife1 = limitedLife1.loadCycles;
            const nLimitedLife2 = !limitedLife2 ? nLimitedLife1 : limitedLife2.loadCycles;
            const nLongLife = longLifeGeneral.loadCycles;

            const sigmaStatic = statics.pittingStressLimit;
            const sigmaLimitedLife1 = limitedLife1.pittingStressLimit;
            const sigmaLimitedLife2 = !limitedLife2 ? sigmaLimitedLife1 : limitedLife2.pittingStressLimit;
            const sigmaLongLifeGeneral = longLifeGeneral.pittingStressLimit;
            const sigmaLongLifeCritical = longLifeCritical.pittingStressLimit;

            // load cycle arrays
            const cyclesStatic = [0, nStatic];
            const cyclesLimitedLife1 = math.logspace(nStatic, nLimitedLife1);
            const cyclesLimitedLife2 = math.logspace(nLimitedLife1, nLimitedLife2);
            const cyclesLongLife = math.logspace(nLimitedLife2, nLongLife);

            // define functions to compute stress limits along SN curve
            const stressesLimitedLife1Fcn = math.loglogFcn(nStatic, sigmaStatic, nLimitedLife1, sigmaLimitedLife1);
            const stressesLimitedLife2Fcn = math.loglogFcn(nLimitedLife1, sigmaLimitedLife1, nLimitedLife2, sigmaLimitedLife2);
            const stressesLongLifeGeneralFcn = math.loglogFcn(nLimitedLife2, sigmaLimitedLife2, nLongLife, sigmaLongLifeGeneral);
            const stressesLongLifeCriticalFcn = math.loglogFcn(nLimitedLife2, sigmaLimitedLife2, nLongLife, sigmaLongLifeCritical);

            const stressesStatic = cyclesStatic.map(() => sigmaStatic);
            const stressesLimitedLife1 = cyclesLimitedLife1.map(x => stressesLimitedLife1Fcn(x));
            const stressesLimitedLife2 = cyclesLimitedLife2.map(x => stressesLimitedLife2Fcn(x));
            const stressesLongLifeGeneral = cyclesLongLife.map(x => stressesLongLifeGeneralFcn(x));
            const stressesLongLifeCritical = cyclesLongLife.map(x => stressesLongLifeCriticalFcn(x));

            // concatenate and create points array
            loadCycles = [...cyclesStatic, ...cyclesLimitedLife1, ...cyclesLimitedLife2, ...cyclesLongLife];
            stressLimits = [...stressesStatic, ...stressesLimitedLife1, ...stressesLimitedLife2, ...stressesLongLifeGeneral];
            stressLimitsCritical = [...stressesStatic, ...stressesLimitedLife1, stressesLimitedLife2, ...stressesLongLifeCritical];
          } else {
            const {static: statics, limitedLife, longLifeGeneral, longLifeCritical} = stressLimitsIso633632006;

            const nStatic = statics.loadCycles;
            const nLimitedLife = limitedLife.loadCycles;
            const nLongLife = longLifeGeneral.loadCycles;

            const sigmaStatic = statics.toothRootStressLimit;
            const sigmaLimitedLife = limitedLife.toothRootStressLimit;
            const sigmaLongLifeGeneral = longLifeGeneral.toothRootStressLimit;
            const sigmaLongLifeCritical = longLifeCritical.toothRootStressLimit;

            // load cycle arrays
            const cyclesStatic = [0, nStatic];
            const cyclesLimitedLife = math.logspace(nStatic, nLimitedLife);
            const cyclesLongLife = math.logspace(nLimitedLife, nLongLife);

            // define functions to compute stress limits along SN curve
            const stressesLimitedLifeFcn = math.loglogFcn(nStatic, sigmaStatic, nLimitedLife, sigmaLimitedLife);
            const stressesLongLifeGeneralFcn = math.loglogFcn(nLimitedLife, sigmaLimitedLife, nLongLife, sigmaLongLifeGeneral);
            const stressesLongLifeCriticalFcn = math.loglogFcn(nLimitedLife, sigmaLimitedLife, nLongLife, sigmaLongLifeCritical);

            const stressesStatic = cyclesStatic.map(() => sigmaStatic);
            const stressesLimitedLife = cyclesLimitedLife.map(x => stressesLimitedLifeFcn(x));
            const stressesLongLifeGeneral = cyclesLongLife.map(x => stressesLongLifeGeneralFcn(x));
            const stressesLongLifeCritical = cyclesLongLife.map(x => stressesLongLifeCriticalFcn(x));

            // concatenate and create points array
            loadCycles = [...cyclesStatic, ...cyclesLimitedLife, ...cyclesLongLife];
            stressLimits = [...stressesStatic, ...stressesLimitedLife, ...stressesLongLifeGeneral];
            stressLimitsCritical = [...stressesStatic, ...stressesLimitedLife, ...stressesLongLifeCritical];
          }

          // endregion

          // update chart data
          this.chartIso.data.datasets[0].data = points(loadCycles, stressLimits);
          this.chartIso.data.datasets[1].data = points(loadCycles, stressLimitsCritical);
          this.chartIso.update();
        }
      },
    },
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/layout-tab-pane-1-2.scss"/>
<style scoped lang="scss">
    .header-buttons {
        display: grid;
        grid-auto-flow: column;
        grid-column-gap: 10px;
        align-items: center;
        margin-right: 10px;
    }
    th, td {
        border-bottom: solid var(--border-color) 1px;
    }
    td {
        word-break: break-word;
        text-align: left;
    }

    .ml-2 {
        margin-left: .5rem;
    }
    .overflow-y {
        overflow-y: auto;
    }

    .collapse-header {
        display: grid;
        grid-template-columns: auto 1fr;
        justify-items: right;
        margin-right: .5rem;
        width: 100%;

        span:first-of-type {
            color: var(--color);
            font-weight: 600;
        }
        span:last-of-type {
            color: var(--color-secondary);
        }
        .el-icon-check {
            color: var(--background);
        }
        .el-icon-check.check-active {
            color: var(--color-hover);
        }
    }

    $select-height: 28px;
    $figure-height: calc(100% - #{$select-height});
    .figure-wrapper {
        height: $figure-height;
        text-align: center;
        img {
            height: 100%;
            object-fit: contain;
            max-width: 100%;  /*resizable pane*/
        }
    }
    .chart-wrapper {
        width: 100%;
        height: calc(100% - 1rem);
        margin: .75rem 0 .25rem;
    }
    .figure-empty {
        height: $figure-height;
        display: grid;
        justify-items: center;
        align-items: center;
        font-size: 40pt;
        color: var(--color-placeholder);
        background: var(--background-image);
    }

    /* OVERRIDE */
    .pane-grid-1-2 {
        grid-template-columns: 50% 50%;
        grid-template-rows: 1fr 1fr;  /*resizeable panes*/

        .pane-1 {
            display: block;
        }
        .pane-2 {
            margin-right: 10pt;  /*avoids cutoff, but unsure why it is needed*/

            /*resizeable pane*/
            resize: vertical;
            overflow: hidden;
            min-height: 100%;
        }
        .pane-3 {
            margin-top: 0;
            border-top: var(--border-color) solid 2px;

            /*resizeable pane*/
            overflow-y: auto;
        }
    }
</style>
